import React, { useEffect, useState } from "react";
import {
  Check,
  Copy,
  Heart,
  ImagePlus,
  LockKeyhole,
  MessageCircle,
  Send,
  Share2,
  Sparkles,
  Trash2,
} from "lucide-react";
import { supabase } from "./lib/supabase";
import "./PrivateLoveNote.css";
import { SmsComposer } from "@capawesome/capacitor-sms-composer";
import { Capacitor } from "@capacitor/core";
import { NativePurchases, PURCHASE_TYPE } from "@capgo/native-purchases";

const MAX_MESSAGE_LENGTH = 500;
const MAX_LOCAL_PHOTO_SIZE = 10 * 1024 * 1024;
const MAX_UPLOAD_PHOTO_EDGE = 1600;
const KEEPSAKE_PRODUCT_ID = "com.iloveyousomuch.premiumkeepsakes";

const SITE_URL = "https://www.iloveyousomuch.love";

const PRIVATE_NOTE_SESSION_KEY =
  "private-love-note-session";

const ENVELOPE_THEMES = [
  {
    value: "blush",
    label: "Blush",
  },
  {
    value: "red",
    label: "Deep Red",
  },
  {
    value: "navy",
    label: "Navy",
  },
  {
    value: "forest",
    label: "Forest",
  },
  {
    value: "black",
    label: "Black",
  },
  {
    value: "ivory",
    label: "Ivory",
  },
  {
    value: "yellow",
    label: "Yellow",
  },
  {
    value: "purple",
    label: "Purple",
  },
];

const PREMIUM_ENVELOPE_THEMES = [
  {
    value: "romantic-rose",
    label: "Romantic Rose",
  },
  {
    value: "golden-love",
    label: "Golden Love",
  },
  {
    value: "midnight-love",
    label: "Midnight Love",
  },
  {
    value: "lavender-love",
    label: "Lavender Love",
  },

  {
  value: "silver-love",
  label: "Silver Love",
},

{
  value: "peach-love",
  label: "Peach Love",
},
];

const ALL_ENVELOPE_THEMES = [
  ...ENVELOPE_THEMES,
  ...PREMIUM_ENVELOPE_THEMES,
];

const VALID_ENVELOPE_THEMES =
  ALL_ENVELOPE_THEMES.map(
    (theme) => theme.value
  );

const PREMIUM_ENVELOPE_VALUES =
  PREMIUM_ENVELOPE_THEMES.map(
    (theme) => theme.value
  );

const isPremiumEnvelopeTheme = (value) =>
  PREMIUM_ENVELOPE_VALUES.includes(value);

const REVEAL_THEME_STYLES = {
  blush: {
    "--reveal-accent": "#b84f6b",
    "--reveal-accent-dark": "#8f3c53",
    "--reveal-soft": "#fff1f4",
    "--reveal-page": "#fff9f8",
    "--reveal-border": "#efd8de",
    "--reveal-text": "#3e2c31",
    "--reveal-muted": "#8c747b",
  },

  red: {
    "--reveal-accent": "#983f4d",
    "--reveal-accent-dark": "#70303a",
    "--reveal-soft": "#f9e7e9",
    "--reveal-page": "#fff8f7",
    "--reveal-border": "#e5c7cc",
    "--reveal-text": "#442c30",
    "--reveal-muted": "#80676b",
  },

  navy: {
    "--reveal-accent": "#314766",
    "--reveal-accent-dark": "#22344d",
    "--reveal-soft": "#eaf0f7",
    "--reveal-page": "#f6f8fb",
    "--reveal-border": "#cad7e6",
    "--reveal-text": "#26364d",
    "--reveal-muted": "#63748a",
  },

  forest: {
    "--reveal-accent": "#42624e",
    "--reveal-accent-dark": "#304839",
    "--reveal-soft": "#eaf2ec",
    "--reveal-page": "#f7faf7",
    "--reveal-border": "#ccdcd0",
    "--reveal-text": "#304238",
    "--reveal-muted": "#68786e",
  },

  black: {
    "--reveal-accent": "#292929",
    "--reveal-accent-dark": "#111111",
    "--reveal-soft": "#ececec",
    "--reveal-page": "#f7f7f7",
    "--reveal-border": "#d0d0d0",
    "--reveal-text": "#262626",
    "--reveal-muted": "#707070",
  },

  ivory: {
    "--reveal-accent": "#7d6659",
    "--reveal-accent-dark": "#5e4c42",
    "--reveal-soft": "#f4ecdd",
    "--reveal-page": "#fbf8f1",
    "--reveal-border": "#ded2bf",
    "--reveal-text": "#493b34",
    "--reveal-muted": "#85756c",
  },

  yellow: {
    "--reveal-accent": "#f2b705",
    "--reveal-accent-dark": "#c98f00",
    "--reveal-soft": "#fff3a6",
    "--reveal-page": "#fffdee",
    "--reveal-border": "#f0cf52",
    "--reveal-text": "#49370f",
    "--reveal-muted": "#7b6527",
  },

  purple: {
    "--reveal-accent": "#72517f",
    "--reveal-accent-dark": "#573d61",
    "--reveal-soft": "#f3ebf6",
    "--reveal-page": "#fbf8fc",
    "--reveal-border": "#ddcce4",
    "--reveal-text": "#403245",
    "--reveal-muted": "#7a6a7e",
  },

  "romantic-rose": {
    "--reveal-accent": "#a83f60",
    "--reveal-accent-dark": "#7f2e47",
    "--reveal-soft": "#fbe8ee",
    "--reveal-page": "#fff8fa",
    "--reveal-border": "#e9c5d0",
    "--reveal-text": "#482b35",
    "--reveal-muted": "#896a74",
  },
  "golden-love": {
    "--reveal-accent": "#9b742d",
    "--reveal-accent-dark": "#73531e",
    "--reveal-soft": "#fbf2dc",
    "--reveal-page": "#fffdf7",
    "--reveal-border": "#e8d4a5",
    "--reveal-text": "#493b26",
    "--reveal-muted": "#85775d",
  },
  "midnight-love": {
    "--reveal-accent": "#3f5278",
    "--reveal-accent-dark": "#273653",
    "--reveal-soft": "#e9edf5",
    "--reveal-page": "#f8f9fc",
    "--reveal-border": "#c9d1e1",
    "--reveal-text": "#263149",
    "--reveal-muted": "#667189",
  },
  "lavender-love": {
    "--reveal-accent": "#80618f",
    "--reveal-accent-dark": "#5f456c",
    "--reveal-soft": "#f1e9f5",
    "--reveal-page": "#fcf9fd",
    "--reveal-border": "#d9c8e1",
    "--reveal-text": "#44364b",
    "--reveal-muted": "#7d6c83",
  },
  "silver-love": {
    "--reveal-accent": "#66707c",
    "--reveal-accent-dark": "#454d57",
    "--reveal-soft": "#eef0f3",
    "--reveal-page": "#fbfcfd",
    "--reveal-border": "#d2d6dc",
    "--reveal-text": "#353b43",
    "--reveal-muted": "#747c86",
  },
  "peach-love": {
    "--reveal-accent": "#b76536",
    "--reveal-accent-dark": "#8c4928",
    "--reveal-soft": "#fff0e4",
    "--reveal-page": "#fffaf6",
    "--reveal-border": "#efcfb8",
    "--reveal-text": "#50362b",
    "--reveal-muted": "#8a7167",
  },
};

const getSafeTheme = (value) =>
  VALID_ENVELOPE_THEMES.includes(value)
    ? value
    : "blush";

const loadPrivateNoteSession = () => {
  try {
    const saved =
      window.localStorage.getItem(
        PRIVATE_NOTE_SESSION_KEY
      );

    if (!saved) {
      return null;
    }

    const parsed = JSON.parse(saved);

    return {
      recipient:
        typeof parsed.recipient === "string"
          ? parsed.recipient
          : "",

      message:
        typeof parsed.message === "string"
          ? parsed.message
          : "",

      author:
        typeof parsed.author === "string"
          ? parsed.author
          : "",

      envelopeTheme:
        getSafeTheme(
          parsed.envelopeTheme
        ),
    };
  } catch {
    return null;
  }
};

const savePrivateNoteSession = (data) => {
  try {
    window.localStorage.setItem(
      PRIVATE_NOTE_SESSION_KEY,
      JSON.stringify(data)
    );
  } catch {
    // The page can still work if storage is unavailable.
  }
};

const clearPrivateNoteSession = () => {
  try {
    window.localStorage.removeItem(
      PRIVATE_NOTE_SESSION_KEY
    );
  } catch {
    // Nothing else is required.
  }
};

function PrivateLoveNote() {
  const isNativeApp = Capacitor.isNativePlatform();

  const pathParts =
    window.location.pathname
      .split("/")
      .filter(Boolean);

  const isRevealPage =
    pathParts[0] === "love" &&
    Boolean(pathParts[1]) &&
    Boolean(pathParts[2]);

  const themeFromPath =
    isRevealPage
      ? pathParts[1]
      : null;

  const shareToken =
    isRevealPage
      ? pathParts[2]
      : null;

  const revealTheme =
    getSafeTheme(themeFromPath);

  const savedSession =
    !isRevealPage
      ? loadPrivateNoteSession()
      : null;

  const [recipient, setRecipient] =
    useState(
      savedSession?.recipient || ""
    );

  const [message, setMessage] =
    useState(
      savedSession?.message || ""
    );

  const [author, setAuthor] =
    useState(
      savedSession?.author || ""
    );

  const [
    envelopeTheme,
    setEnvelopeTheme,
  ] = useState(
    getSafeTheme(
      savedSession?.envelopeTheme ||
        "blush"
    )
  );

  const [
    composerMode,
    setComposerMode,
  ] = useState("edit");

  const [
    previewOpened,
    setPreviewOpened,
  ] = useState(false);

  const [submitting, setSubmitting] =
    useState(false);

  const [
    composerMessage,
    setComposerMessage,
  ] = useState("");

  const [
    createdShareLink,
    setCreatedShareLink,
  ] = useState("");

  const [copied, setCopied] =
    useState(false);

  const [
    privateNote,
    setPrivateNote,
  ] = useState(null);

  const [
    loadingNote,
    setLoadingNote,
  ] = useState(isRevealPage);

  const [
    noteError,
    setNoteError,
  ] = useState("");

  const [
    noteOpened,
    setNoteOpened,
  ] = useState(false);

  const [
    keepsakePhoto,
    setKeepsakePhoto,
  ] = useState(null);

  const [
    keepsakePhotoUrl,
    setKeepsakePhotoUrl,
  ] = useState("");

  const [
    keepsakePhotoMessage,
    setKeepsakePhotoMessage,
  ] = useState("");

  const [
    createdShareToken,
    setCreatedShareToken,
  ] = useState("");

  const [
    photoUploadState,
    setPhotoUploadState,
  ] = useState("idle");

  const [
    recipientPhotoUrl,
    setRecipientPhotoUrl,
  ] = useState("");

  const [
    recipientPhotoLoading,
    setRecipientPhotoLoading,
  ] = useState(false);

  const [
    recipientPhotoError,
    setRecipientPhotoError,
  ] = useState("");

  const [
    keepsakeUnlocked,
    setKeepsakeUnlocked,
  ] = useState(false);

  const [
    keepsakeStorePrice,
    setKeepsakeStorePrice,
  ] = useState("");

  const [
    keepsakePurchaseState,
    setKeepsakePurchaseState,
  ] = useState("loading");

  const [
    keepsakePurchaseMessage,
    setKeepsakePurchaseMessage,
  ] = useState("");

  const [
    keepsakeTransactionId,
    setKeepsakeTransactionId,
  ] = useState("");

  const charactersLeft =
    MAX_MESSAGE_LENGTH -
    message.length;

  const cleanRecipient =
    recipient.trim();

  const cleanMessage =
    message.trim();

  const cleanAuthor =
    author.trim();

  const canCreateNote =
    Boolean(
      cleanRecipient &&
      cleanMessage
    );


  const premiumThemeSelected =
    isPremiumEnvelopeTheme(envelopeTheme);


  const findKeepsakePurchase = (purchases = []) =>
    purchases.find((purchase) =>
      purchase?.productIdentifier === KEEPSAKE_PRODUCT_ID ||
      purchase?.identifier === KEEPSAKE_PRODUCT_ID
    ) || null;

  // eslint-disable-next-line no-unused-vars
const hasKeepsakePurchase = (purchases = []) =>
  Boolean(findKeepsakePurchase(purchases));

  const refreshKeepsakeEntitlement = async () => {
    const { purchases = [] } =
      await NativePurchases.getPurchases({
        productType: PURCHASE_TYPE.INAPP,
      });

    const purchase = findKeepsakePurchase(purchases);
    const unlocked = Boolean(purchase);

    setKeepsakeUnlocked(unlocked);
    setKeepsakeTransactionId(
      purchase?.transactionId
        ? String(purchase.transactionId)
        : ""
    );

    return purchase;
  };

  const purchaseKeepsakeCollection = async () => {
    if (!isNativeApp || keepsakeUnlocked) {
      return;
    }

    setKeepsakePurchaseState("purchasing");
    setKeepsakePurchaseMessage("");

    try {
      const purchase =
        await NativePurchases.purchaseProduct({
          productIdentifier: KEEPSAKE_PRODUCT_ID,
          productType: PURCHASE_TYPE.INAPP,
          quantity: 1,
        });

      if (purchase?.transactionId) {
        setKeepsakeTransactionId(
          String(purchase.transactionId)
        );
      }

      const confirmedPurchase =
        await refreshKeepsakeEntitlement();

      if (!confirmedPurchase) {
        throw new Error(
          "The purchase finished, but the Keepsake Collection could not be confirmed yet."
        );
      }

      setKeepsakePurchaseState("ready");
      setKeepsakePurchaseMessage(
        "The Keepsake Collection is unlocked. All six designs are yours to keep."
      );
    } catch (error) {
      const message = String(
        error?.message || error || ""
      ).toLowerCase();

      if (
        message.includes("cancel") ||
        message.includes("user cancelled")
      ) {
        setKeepsakePurchaseMessage(
          "Purchase canceled. Nothing was charged."
        );
      } else {
        console.error(
          "Unable to purchase Keepsake Collection:",
          error
        );
        setKeepsakePurchaseMessage(
          "The purchase could not be completed. Please try again."
        );
      }

      setKeepsakePurchaseState("ready");
    }
  };

  const restoreKeepsakePurchases = async () => {
    if (!isNativeApp) {
      return;
    }

    setKeepsakePurchaseState("restoring");
    setKeepsakePurchaseMessage("");

    try {
      await NativePurchases.restorePurchases();
      const restoredPurchase =
        await refreshKeepsakeEntitlement();

      setKeepsakePurchaseMessage(
        restoredPurchase
          ? "Your Keepsake Collection purchase has been restored."
          : "No previous Keepsake Collection purchase was found for this Apple ID."
      );
    } catch (error) {
      console.error(
        "Unable to restore Keepsake purchase:",
        error
      );
      setKeepsakePurchaseMessage(
        "Purchases could not be restored right now. Please try again."
      );
    } finally {
      setKeepsakePurchaseState("ready");
    }
  };

  useEffect(() => {
    if (!isNativeApp || isRevealPage) {
      return;
    }

    let cancelled = false;

    const initializeKeepsakeStore = async () => {
      setKeepsakePurchaseState("loading");
      setKeepsakePurchaseMessage("");

      try {
        const { isBillingSupported } =
          await NativePurchases.isBillingSupported();

        if (!isBillingSupported) {
          if (!cancelled) {
            setKeepsakePurchaseState("unavailable");
            setKeepsakePurchaseMessage(
              "Apple purchases are not available on this device right now."
            );
          }
          return;
        }

        const { products = [] } =
          await NativePurchases.getProducts({
            productIdentifiers: [KEEPSAKE_PRODUCT_ID],
            productType: PURCHASE_TYPE.INAPP,
          });

        const product = products.find(
          (item) =>
            item?.identifier === KEEPSAKE_PRODUCT_ID ||
            item?.productIdentifier === KEEPSAKE_PRODUCT_ID
        );

        const { purchases = [] } =
          await NativePurchases.getPurchases({
            productType: PURCHASE_TYPE.INAPP,
          });

        if (cancelled) {
          return;
        }

        setKeepsakeStorePrice(
          product?.priceString || ""
        );
        const keepsakePurchase =
          findKeepsakePurchase(purchases);

        setKeepsakeUnlocked(
          Boolean(keepsakePurchase)
        );
        setKeepsakeTransactionId(
          keepsakePurchase?.transactionId
            ? String(keepsakePurchase.transactionId)
            : ""
        );
        setKeepsakePurchaseState("ready");
      } catch (error) {
        console.error(
          "Unable to initialize Keepsake purchases:",
          error
        );

        if (!cancelled) {
          setKeepsakePurchaseState("unavailable");
          setKeepsakePurchaseMessage(
            "The Keepsake Collection could not connect to the App Store yet."
          );
        }
      }
    };

    initializeKeepsakeStore();

    return () => {
      cancelled = true;
    };
  }, [isNativeApp, isRevealPage]);

  useEffect(() => {
    if (isRevealPage) {
      return;
    }

    if (composerMode === "ready") {
      return;
    }

    savePrivateNoteSession({
      recipient,
      message,
      author,
      envelopeTheme,
    });
  }, [
    recipient,
    message,
    author,
    envelopeTheme,
    composerMode,
    isRevealPage,
  ]);

  useEffect(() => {
    if (
      !isRevealPage ||
      !shareToken
    ) {
      return;
    }

    let cancelled = false;

    const loadPrivateNote =
      async () => {
        setLoadingNote(true);
        setNoteError("");

        const { data, error } =
          await supabase.rpc(
            "get_private_love_note",
            {
              p_share_token:
                shareToken,
            }
          );

        if (cancelled) {
          return;
        }

        if (error) {
          console.error(
            "Unable to load private love note:",
            error
          );

          setNoteError(
            "This private love note couldn't be opened."
          );

          setLoadingNote(false);
          return;
        }

        const note =
          Array.isArray(data)
            ? data[0]
            : null;

        if (!note) {
          setNoteError(
            "This private love note doesn't seem to exist."
          );

          setLoadingNote(false);
          return;
        }

        setPrivateNote(note);
        setLoadingNote(false);
      };

    loadPrivateNote();

    return () => {
      cancelled = true;
    };
  }, [
    isRevealPage,
    shareToken,
  ]);

  useEffect(() => {
    return () => {
      if (keepsakePhotoUrl) {
        URL.revokeObjectURL(keepsakePhotoUrl);
      }
    };
  }, [keepsakePhotoUrl]);

  useEffect(() => {
    if (
      !isRevealPage ||
      !shareToken ||
      !privateNote?.photo_path
    ) {
      return;
    }

    let cancelled = false;

    const loadKeepsakePhoto = async () => {
      setRecipientPhotoLoading(true);
      setRecipientPhotoError("");

      const { data, error } =
        await supabase.functions.invoke(
          "get-keepsake-photo",
          {
            body: {
              shareToken,
            },
          }
        );

      if (cancelled) {
        return;
      }

      if (error || !data?.photoUrl) {
        console.error(
          "Unable to load Keepsake photo:",
          error || data
        );

        setRecipientPhotoError(
          "This Keepsake photo could not be loaded."
        );
        setRecipientPhotoLoading(false);
        return;
      }

      setRecipientPhotoUrl(data.photoUrl);
      setRecipientPhotoLoading(false);
    };

    loadKeepsakePhoto();

    return () => {
      cancelled = true;
    };
  }, [
    isRevealPage,
    shareToken,
    privateNote?.photo_path,
  ]);

  const prepareKeepsakePhotoForUpload =
    async (file) => {
      const sourceUrl = URL.createObjectURL(file);

      try {
        const image = new Image();

        await new Promise((resolve, reject) => {
          image.onload = resolve;
          image.onerror = reject;
          image.src = sourceUrl;
        });

        const longestEdge = Math.max(
          image.naturalWidth,
          image.naturalHeight
        );

        const scale =
          longestEdge > MAX_UPLOAD_PHOTO_EDGE
            ? MAX_UPLOAD_PHOTO_EDGE / longestEdge
            : 1;

        const width = Math.max(
          1,
          Math.round(image.naturalWidth * scale)
        );
        const height = Math.max(
          1,
          Math.round(image.naturalHeight * scale)
        );

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;

        const context = canvas.getContext("2d");

        if (!context) {
          throw new Error("Canvas is unavailable.");
        }

        context.drawImage(image, 0, 0, width, height);

        const blob = await new Promise((resolve, reject) => {
          canvas.toBlob(
            (result) =>
              result
                ? resolve(result)
                : reject(
                    new Error("Unable to prepare photo.")
                  ),
            "image/jpeg",
            0.86
          );
        });

        return new File(
          [blob],
          "keepsake-photo.jpg",
          {
            type: "image/jpeg",
          }
        );
      } finally {
        URL.revokeObjectURL(sourceUrl);
      }
    };

  const uploadKeepsakePhoto =
    async (token, file) => {
      if (!token || !file) {
        return false;
      }

      setPhotoUploadState("uploading");
      setKeepsakePhotoMessage(
        "Preparing your Keepsake photo..."
      );

      try {
        const uploadFile =
          await prepareKeepsakePhotoForUpload(file);

        const formData = new FormData();
        formData.append("photo", uploadFile);
        formData.append("shareToken", token);

        const { data, error } =
          await supabase.functions.invoke(
            "upload-keepsake-photo",
            {
              body: formData,
            }
          );

        if (error || !data?.ok) {
          throw error || new Error(
            data?.error || "Photo upload failed."
          );
        }

        setPhotoUploadState("success");
        setKeepsakePhotoMessage(
          "Your photo is safely tucked inside the Keepsake."
        );

        return true;
      } catch (error) {
        console.error(
          "Unable to upload Keepsake photo:",
          error
        );

        setPhotoUploadState("error");
        setKeepsakePhotoMessage(
          "Your note was created, but the photo did not upload. You can try again before sharing the link."
        );

        return false;
      }
    };

  const handleKeepsakePhotoChange = (event) => {
    const file = event.target.files?.[0];

    event.target.value = "";

    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      setKeepsakePhotoMessage(
        "Please choose an image from your photo library."
      );
      return;
    }

    if (file.size > MAX_LOCAL_PHOTO_SIZE) {
      setKeepsakePhotoMessage(
        "That photo is a little too large for this preview. Please choose one under 10 MB."
      );
      return;
    }

    if (keepsakePhotoUrl) {
      URL.revokeObjectURL(keepsakePhotoUrl);
    }

    const objectUrl = URL.createObjectURL(file);

    setKeepsakePhoto(file);
    setKeepsakePhotoUrl(objectUrl);
    setKeepsakePhotoMessage("");
  };

  const removeKeepsakePhoto = () => {
    if (keepsakePhotoUrl) {
      URL.revokeObjectURL(keepsakePhotoUrl);
    }

    setKeepsakePhoto(null);
    setKeepsakePhotoUrl("");
    setKeepsakePhotoMessage("");
  };

  const handlePreview = () => {
    if (!canCreateNote && !premiumThemeSelected) {
      return;
    }

    setComposerMessage("");
    setPreviewOpened(false);
    setComposerMode("preview");

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const returnToEdit = () => {
    setPreviewOpened(false);
    setComposerMode("edit");

    window.setTimeout(() => {
      document
        .getElementById(
          "private-recipient"
        )
        ?.focus({
          preventScroll: true,
        });
    }, 0);
  };

  const createPrivateNote =
    async () => {
      if (
        submitting ||
        !canCreateNote
      ) {
        return;
      }

      if (
        premiumThemeSelected &&
        !keepsakeUnlocked
      ) {
        setComposerMessage(
          "Unlock the Keepsake Collection before creating this premium note."
        );
        return;
      }

      const selectedTheme =
        getSafeTheme(
          envelopeTheme
        );

      setSubmitting(true);
      setComposerMessage("");
      setCreatedShareLink("");
      setCopied(false);

      let token = "";

      if (premiumThemeSelected) {
        let transactionId =
          keepsakeTransactionId;

        if (!transactionId) {
          try {
            const purchase =
              await refreshKeepsakeEntitlement();

            transactionId =
              purchase?.transactionId
                ? String(purchase.transactionId)
                : "";
          } catch (error) {
            console.error(
              "Unable to refresh Keepsake purchase before creation:",
              error
            );
          }
        }

        if (!transactionId) {
          setComposerMessage(
            "Your Keepsake purchase could not be verified yet. Please restore purchases and try again."
          );
          setSubmitting(false);
          return;
        }

        const { data, error } =
          await supabase.functions.invoke(
            "create-keepsake-note",
            {
              body: {
                recipient: cleanRecipient,
                message: cleanMessage,
                authorName: cleanAuthor || null,
                envelopeTheme: selectedTheme,
                transactionId,
              },
            }
          );

        if (error || !data?.shareToken) {
          console.error(
            "Unable to create verified Keepsake note:",
            error || data
          );

          setComposerMessage(
            data?.error ||
              "Your Keepsake purchase could not be verified just yet. Please try again."
          );

          setSubmitting(false);
          return;
        }

        token = String(data.shareToken);
      } else {
        const { data, error } =
          await supabase.rpc(
            "create_private_love_note",
            {
              p_recipient:
                cleanRecipient,

              p_message:
                cleanMessage,

              p_author_name:
                cleanAuthor || null,

              p_envelope_theme:
                selectedTheme,
            }
          );

        if (error) {
          console.error(
            "Unable to create private love note:",
            error
          );

          setComposerMessage(
            "Your private note couldn't be created just yet. Please try again."
          );

          setSubmitting(false);
          return;
        }

        token = String(data);
      }

      const shareLink =
        `${SITE_URL}/love/` +
        `${selectedTheme}/` +
        `${token}`;

      setRecipient(
        cleanRecipient
      );

      setMessage(
        cleanMessage
      );

      setAuthor(
        cleanAuthor
      );

      setEnvelopeTheme(
        selectedTheme
      );

      setCreatedShareToken(token);
      setCreatedShareLink(
        shareLink
      );

      if (
        premiumThemeSelected &&
        keepsakePhoto
      ) {
        await uploadKeepsakePhoto(
          token,
          keepsakePhoto
        );
      } else {
        setPhotoUploadState("idle");
        setKeepsakePhotoMessage("");
      }

      clearPrivateNoteSession();

      setPreviewOpened(false);
      setComposerMode("ready");
      setSubmitting(false);

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    };

  const handleCreateSubmit = (
    event
  ) => {
    event.preventDefault();

    createPrivateNote();
  };

  const copyShareLink =
    async () => {
      if (!createdShareLink) {
        return;
      }

      try {
        await navigator.clipboard.writeText(
          createdShareLink
        );

        setCopied(true);

        window.setTimeout(() => {
          setCopied(false);
        }, 2500);
      } catch (error) {
        console.error(
          "Unable to copy private note link:",
          error
        );
      }
    };

  const sharePrivateNote =
    async () => {
      if (!createdShareLink) {
        return;
      }

      const shareText =
        `💗 Someone made you a private love note\n\n` +
        `Open your surprise here:\n${createdShareLink}`;

      try {
        if (navigator.share) {
          await navigator.share({
            title:
              "A private love note for you",

            text: shareText,
          });

          return;
        }

        await navigator.clipboard.writeText(
          shareText
        );

        setCopied(true);

        window.setTimeout(() => {
          setCopied(false);
        }, 2500);
      } catch (error) {
        if (
          error?.name !==
          "AbortError"
        ) {
          console.error(
            "Unable to share private note:",
            error
          );
        }
      }
    };

  const textPrivateNote =
    async () => {
      if (!createdShareLink) {
        return;
      }

      const textMessage =
        `💗 Someone made you a private love note\n\n` +
        `Open your surprise here:\n${createdShareLink}`;

      try {
        if (
          Capacitor.isNativePlatform()
        ) {
          const {
            canCompose,
          } =
            await SmsComposer.canComposeSms();

          if (!canCompose) {
            console.error(
              "This device cannot compose SMS messages."
            );

            return;
          }

          await SmsComposer.composeSms({
            body: textMessage,
          });

          return;
        }

        if (navigator.share) {
          await navigator.share({
            title:
              "A private love note for you",
            text: textMessage,
          });

          return;
        }

        await navigator.clipboard.writeText(
          textMessage
        );

        setCopied(true);

        window.setTimeout(() => {
          setCopied(false);
        }, 2500);
      } catch (error) {
        if (
          error?.name !==
          "AbortError"
        ) {
          console.error(
            "Unable to share private love note:",
            error
          );
        }
      }
    };

  const openLoveNote = () => {
    setNoteOpened(true);
  };

  const startAnotherNote = () => {
    clearPrivateNoteSession();

    setRecipient("");
    setMessage("");
    setAuthor("");
    setEnvelopeTheme("blush");

    setCreatedShareToken("");
    setCreatedShareLink("");
    setComposerMessage("");
    setPhotoUploadState("idle");
    setRecipientPhotoUrl("");
    setRecipientPhotoError("");
    setCopied(false);

    setPreviewOpened(false);
    setComposerMode("edit");

    removeKeepsakePhoto();

    window.location.assign("/private");
  };

  const returnToCommunityWall = () => {
    window.location.assign("/");
  };

  if (isRevealPage) {
    return (
      <main
        className={
          `privateLovePage privateLoveRevealPage ` +
          `revealTheme-${revealTheme}`
        }
        style={
          REVEAL_THEME_STYLES[
            revealTheme
          ] ||
          REVEAL_THEME_STYLES.blush
        }
      >
        <section className="privateReveal">
          <div className="privateHeartCluster">
            <Heart
              size={15}
              fill="currentColor"
            />

            <Heart
              size={27}
              fill="currentColor"
            />

            <Heart
              size={13}
              fill="currentColor"
            />
          </div>

          {loadingNote && (
            <div className="privateLoading">
              <Heart
                size={22}
                fill="currentColor"
              />

              Opening something meant just for you...
            </div>
          )}

          {!loadingNote &&
            noteError && (
              <div className="privateError">
                <Heart size={25} />

                <h1>
                  This little note is hiding.
                </h1>

                <p>
                  {noteError}
                </p>

                <button
                  type="button"
                  className="privateSecondaryButton"
                  onClick={
                    returnToCommunityWall
                  }
                >
                  Visit the Community Wall
                </button>
              </div>
            )}

          {!loadingNote &&
            privateNote && (
              <>
                {!noteOpened ? (
                  <div className="privateEnvelopeReveal">
                    <p className="privateEyebrow">
                      Something was sent just for you
                    </p>

                    <h1>
                      There&apos;s a little love waiting inside.
                    </h1>

                    <p className="privateRevealIntro">
                      Tap the envelope whenever
                      you&apos;re ready.
                    </p>

                    <button
                      type="button"
                      className={
                        `loveEnvelopeButton ` +
                        `loveEnvelope-${revealTheme}`
                      }
                      onClick={
                        openLoveNote
                      }
                      aria-label="Open your private love note"
                    >
                      <span
                        className="loveEnvelope"
                        aria-hidden="true"
                      >
                        <span className="loveEnvelopeBack" />

                        <span className="loveEnvelopeLetter">
                          <Heart
                            size={18}
                            fill="currentColor"
                          />

                          <span>
                            For you
                          </span>
                        </span>

                        <span className="loveEnvelopeFront" />

                        <span className="loveEnvelopeFlap" />

                        <span className="loveEnvelopeSeal">
                          <Heart
                            size={18}
                            fill="currentColor"
                          />
                        </span>
                      </span>

                      <span className="loveEnvelopeInstruction">
                        <Heart
                          size={14}
                          fill="currentColor"
                        />

                        Tap to open your love note
                      </span>
                    </button>
                  </div>
                ) : (
                  <div className="openedLoveNote">
                    <p className="privateEyebrow">
                      Someone wanted you to have this
                    </p>

                    <h1>
                      Someone loves you very much.
                    </h1>

                    <p className="privateRevealIntro">
                      This little corner of the internet
                      was made just for you.
                    </p>

                    <article
                      className={
                        `privateNoteCard privateNoteCardOpened ` +
                        `theme-${revealTheme}`
                      }
                    >
                      <Sparkles
                        className="privateSparkle"
                        size={21}
                      />

                      {privateNote.photo_path &&
                        recipientPhotoLoading && (
                          <p className="keepsakePhotoLoading">
                            Preparing a favorite memory...
                          </p>
                        )}

                      {privateNote.photo_path &&
                        recipientPhotoUrl && (
                          <figure
                            className={
                              `keepsakePhotoFrame keepsakePhotoFrame-${revealTheme}`
                            }
                          >
                            <img
                              src={recipientPhotoUrl}
                              alt="A photograph included with this private Keepsake love note"
                            />

                            <figcaption>
                              A favorite memory, tucked inside.
                            </figcaption>
                          </figure>
                        )}

                      {privateNote.photo_path &&
                        recipientPhotoError && (
                          <p className="keepsakePhotoLoading">
                            The photo is resting for a moment, but the love note is still here.
                          </p>
                        )}

                      <p className="privateRecipient">
                        For{" "}
                        {
                          privateNote.recipient
                        }
                      </p>

                      <blockquote>
                        “
                        {
                          privateNote.message
                        }
                        ”
                      </blockquote>

                      <p className="privateAuthor">
                        —{" "}
                        {
                          privateNote.author_name ||
                          "Someone who loves you"
                        }
                      </p>
                    </article>

                    <p className="privatePrivacyMessage">
                      <LockKeyhole
                        size={14}
                      />

                      This note was shared privately
                      and does not appear on the
                      Community Wall.
                    </p>

                    <div className="privateRevealActions">
                      <button
                        type="button"
                        className="privateSecondaryButton"
                        onClick={
                          startAnotherNote
                        }
                      >
                        Send a free private love note
                      </button>

                      <p className="recipientAppNote">
  The Keepsake Collection is available in the I Love You So Much app.
</p>

                      <button
                        type="button"
                        className="privateHomeLink privateHomeLinkButton"
                        onClick={
                          returnToCommunityWall
                        }
                      >
                        Return to the home page
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
        </section>
      </main>
    );
  }

  if (composerMode === "preview") {
    const previewTheme =
      getSafeTheme(
        envelopeTheme
      );

    return (
      <main
        className={
          `privateLovePage privateLoveRevealPage ` +
          `revealTheme-${previewTheme}`
        }
        style={
          REVEAL_THEME_STYLES[
            previewTheme
          ] ||
          REVEAL_THEME_STYLES.blush
        }
      >
        <section className="privateReveal">
          <div className="privateHeartCluster">
            <Heart
              size={15}
              fill="currentColor"
            />

            <Heart
              size={27}
              fill="currentColor"
            />

            <Heart
              size={13}
              fill="currentColor"
            />
          </div>

          {!previewOpened ? (
            <div className="privateEnvelopeReveal">
              <p className="privateEyebrow">
                Preview your private note
              </p>

              <h1>
                This is what they&apos;ll see first.
              </h1>

              <p className="privateRevealIntro">
                Tap the envelope to preview the
                full experience before you create it.
              </p>

              <button
                type="button"
                className={
                  `loveEnvelopeButton ` +
                  `loveEnvelope-${previewTheme}`
                }
                onClick={() =>
                  setPreviewOpened(true)
                }
                aria-label="Open your private love note preview"
              >
                <span
                  className="loveEnvelope"
                  aria-hidden="true"
                >
                  <span className="loveEnvelopeBack" />

                  <span className="loveEnvelopeLetter">
                    <Heart
                      size={18}
                      fill="currentColor"
                    />

                    <span>
                      For you
                    </span>
                  </span>

                  <span className="loveEnvelopeFront" />

                  <span className="loveEnvelopeFlap" />

                  <span className="loveEnvelopeSeal">
                    <Heart
                      size={18}
                      fill="currentColor"
                    />
                  </span>
                </span>

                <span className="loveEnvelopeInstruction">
                  <Heart
                    size={14}
                    fill="currentColor"
                  />

                  Tap to open the preview
                </span>
              </button>
            </div>
          ) : (
            <div className="openedLoveNote">
              <p className="privateEyebrow">
                Preview your private note
              </p>

              <h1>
                This is how your note will look.
              </h1>

              <p className="privateRevealIntro">
                Read it carefully. If you want to
                change anything, return to the form
                and your draft will still be there.
              </p>

              <article
                className={
                  `privateNoteCard privateNoteCardOpened ` +
                  `theme-${previewTheme}`
                }
              >
                <Sparkles
                  className="privateSparkle"
                  size={21}
                />

                {premiumThemeSelected &&
                  keepsakePhotoUrl && (
                    <figure
                      className={
                        `keepsakePhotoFrame keepsakePhotoFrame-${previewTheme}`
                      }
                    >
                      <img
                        src={keepsakePhotoUrl}
                        alt="Keepsake preview selected by the sender"
                      />

                      <figcaption>
                        A favorite memory, tucked inside.
                      </figcaption>
                    </figure>
                  )}

                <p className="privateRecipient">
                  For {cleanRecipient || "Someone special"}
                </p>

                <blockquote>
                  “{cleanMessage ||
                    "This is a preview of how your Keepsake love note will look when someone special opens it."}”
                </blockquote>

                <p className="privateAuthor">
                  —{" "}
                  {cleanAuthor ||
                    "Someone who loves you"}
                </p>
              </article>
            </div>
          )}

          <div className="senderPreviewActions">
            <p>
              Previewing does not create or send
              the note. You can return to the form
              and edit it as many times as you want.
            </p>

            <div className="privatePreviewActionRow">
              <button
                type="button"
                className="privateSecondaryButton"
                onClick={
                  returnToEdit
                }
                disabled={
                  submitting
                }
              >
                ← Back to edit
              </button>

              <button
                type="button"
                className="privatePrimaryButton senderReturnButton"
                onClick={
                  createPrivateNote
                }
                disabled={
                  submitting ||
                  !canCreateNote ||
                  (premiumThemeSelected &&
                    !keepsakeUnlocked)
                }
              >
                <Send size={17} />

                {submitting
                  ? premiumThemeSelected && keepsakePhoto
                    ? "Creating Keepsake & adding photo..."
                    : "Creating your note..."
                  : premiumThemeSelected
                    ? "Create Keepsake"
                    : "Create private love note"}
              </button>
            </div>

            {composerMessage && (
              <p
                className="privateStatus"
                role="status"
              >
                {composerMessage}
              </p>
            )}
          </div>
        </section>
      </main>
    );
  }

  if (
    composerMode === "ready" &&
    createdShareLink
  ) {
    const selectedEnvelopeLabel =
      ALL_ENVELOPE_THEMES.find(
        (theme) =>
          theme.value ===
          envelopeTheme
      )?.label ||
      "selected";

    return (
      <main className="privateLovePage">
        <section className="privateComposer">
          <div className="privateHeartCluster">
            <Heart
              size={15}
              fill="currentColor"
            />

            <Heart
              size={27}
              fill="currentColor"
            />

            <Heart
              size={13}
              fill="currentColor"
            />
          </div>

          <section className="privateLinkCard">
            <div className="privateLinkHeart">
              <Heart
                size={26}
                fill="currentColor"
              />
            </div>

            <p className="privateEyebrow">
              Your private note is ready
            </p>

            <h2>
              Send a little love their way.
            </h2>

            <p>
              Your note has been created.
              Share the private link only with
              the person you intended it for.
            </p>

            {premiumThemeSelected &&
              keepsakePhoto && (
                <div
                  className={
                    photoUploadState === "error"
                      ? "keepsakeUploadStatus keepsakeUploadStatusError"
                      : "keepsakeUploadStatus"
                  }
                  role="status"
                >
                  <p>
                    {keepsakePhotoMessage ||
                      "Your Keepsake photo is ready."}
                  </p>

                  {photoUploadState === "error" &&
                    createdShareToken && (
                      <button
                        type="button"
                        className="privateSecondaryButton"
                        onClick={() =>
                          uploadKeepsakePhoto(
                            createdShareToken,
                            keepsakePhoto
                          )
                        }
                      >
                        Try photo upload again
                      </button>
                    )}
                </div>
              )}

            <div className="privateShareLink">
              <span>
                {createdShareLink}
              </span>

              <div className="privateShareActions">
                <button
                  type="button"
                  onClick={
                    copyShareLink
                  }
                  aria-label="Copy private love note link"
                >
                  {copied ? (
                    <Check size={17} />
                  ) : (
                    <Copy size={17} />
                  )}

                  {copied
                    ? "Copied"
                    : "Copy"}
                </button>

                <button
                  type="button"
                  onClick={
                    sharePrivateNote
                  }
                  aria-label="Share private love note"
                >
                  <Share2 size={17} />

                  Share
                </button>

                <button
                  type="button"
                  onClick={
                    textPrivateNote
                  }
                  aria-label="Send private love note by text message"
                >
                  <MessageCircle
                    size={17}
                  />

                  Text It
                </button>
              </div>
            </div>

            <p className="privateShareHint">
              They&apos;ll open the{" "}
              <strong>
                {selectedEnvelopeLabel} envelope
              </strong>{" "}
              you chose.
            </p>

            <button
              type="button"
              className="privateSecondaryButton"
              onClick={
                startAnotherNote
              }
            >
              Write another one
            </button>
          </section>

          <button
            type="button"
            className="privateBackLink privateBackLinkButton"
            onClick={
              returnToCommunityWall
            }
          >
            ← Back to the Community Wall
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="privateLovePage">
      <section className="privateComposer">
        <div className="privateHeartCluster">
          <Heart
            size={15}
            fill="currentColor"
          />

          <Heart
            size={27}
            fill="currentColor"
          />

          <Heart
            size={13}
            fill="currentColor"
          />
        </div>

        <p className="privateEyebrow">
          For one heart only
        </p>

        <h1>
          Send someone a private little love note.
        </h1>

        <p className="privateIntroduction">
          Write something meant for one person.
          Preview it first if you&apos;d like,
          make any changes you need, and create
          the private link when it feels right.
        </p>

        <form
          className="privateLoveForm"
          onSubmit={
            handleCreateSubmit
          }
        >
          <div className="privateFormField">
            <label htmlFor="private-recipient">
              Who is this for?
            </label>

            <input
              id="private-recipient"
              type="text"
              value={recipient}
              onChange={(event) =>
                setRecipient(
                  event.target.value
                )
              }
              placeholder="My husband, Mom, Sarah..."
              maxLength={70}
              required
            />
          </div>

          <div className="privateFormField">
            <label htmlFor="private-message">
              What do you want them to know?
            </label>

            <textarea
              id="private-message"
              value={message}
              onChange={(event) =>
                setMessage(
                  event.target.value
                )
              }
              placeholder="Write the thing you want only them to read..."
              maxLength={
                MAX_MESSAGE_LENGTH
              }
              required
            />

            <div className="privateCharacterCounter">
              <span>
                Just between the two of you.
              </span>

              <span>
                {charactersLeft} characters left
              </span>
            </div>
          </div>

          <div className="privateFormField">
            <label htmlFor="private-author">
              From

              <span className="privateOptional">
                Optional
              </span>
            </label>

            <input
              id="private-author"
              type="text"
              value={author}
              onChange={(event) =>
                setAuthor(
                  event.target.value
                )
              }
              placeholder="Your name, initial, or leave it mysterious"
              maxLength={40}
            />
          </div>

          <div className="privateEnvelopeSection">
            <div className="privateEnvelopeHeading">
              <span>
                Choose your envelope
              </span>

              <small>
                Make it feel like them.
              </small>
            </div>

            <div
              className="privateEnvelopeChoices"
              role="radiogroup"
              aria-label="Choose envelope color"
            >
              {ENVELOPE_THEMES.map(
                (theme) => {
                  const selected =
                    envelopeTheme ===
                    theme.value;

                  return (
                    <button
                      key={
                        theme.value
                      }
                      type="button"
                      role="radio"
                      aria-checked={
                        selected
                      }
                      className={
                        selected
                          ? `envelopeChoice envelope-${theme.value} envelopeChoiceActive`
                          : `envelopeChoice envelope-${theme.value}`
                      }
                      onClick={() =>
                        setEnvelopeTheme(
                          theme.value
                        )
                      }
                    >
                      <span
                        className="envelopePreview"
                        aria-hidden="true"
                      >
                        <span className="envelopeFlap" />

                        <Heart
                          className="envelopeSeal"
                          size={13}
                          fill="currentColor"
                        />
                      </span>

                      <span className="envelopeChoiceLabel">
                        {theme.label}
                      </span>
                    </button>
                  );
                }
              )}
            </div>

            {isNativeApp && (
              <div className="premiumEnvelopeSection">
                <div className="premiumEnvelopeHeading">
                  <div>
                    <span>The Keepsake Collection</span>
                    <small>
                      <small>
  Six special designs, plus the option to add a favorite photo to your Keepsake.
</small>
                    </small>
                  </div>

                  <span className="premiumEnvelopePrice">
                    {keepsakeStorePrice || "One-time"}
                  </span>
                </div>

                <div
                  className="privateEnvelopeChoices"
                  role="radiogroup"
                  aria-label="The Keepsake Collection envelope designs"
                >
                  {PREMIUM_ENVELOPE_THEMES.map((theme) => {
                    const selected =
                      envelopeTheme === theme.value;

                    return (
                      <button
                        key={theme.value}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        className={
                          selected
                            ? `envelopeChoice premiumEnvelopeChoice envelope-${theme.value} envelopeChoiceActive`
                            : `envelopeChoice premiumEnvelopeChoice envelope-${theme.value}`
                        }
                        onClick={() =>
                          setEnvelopeTheme(theme.value)
                        }
                        aria-label={`${theme.label} — Keepsake Collection envelope`}
                      >
                        <span
                          className="envelopePreview"
                          aria-hidden="true"
                        >
                          <span className="premiumEnvelopeLining" />
                          <span className="envelopeFlap" />

                          <Heart
                            className="envelopeSeal"
                            size={13}
                            fill="currentColor"
                          />

                          <span className="premiumEnvelopeDetail" />

                          {!keepsakeUnlocked && (
                            <span className="premiumEnvelopeLock">
                              <LockKeyhole size={13} />
                            </span>
                          )}
                        </span>

                        <span className="envelopeChoiceLabel">
                          {theme.label}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {premiumThemeSelected && (
                  <section
                    className="keepsakePhotoPicker"
                    aria-labelledby="keepsake-photo-heading"
                  >
                    <div className="keepsakePhotoPickerHeading">
                      <div>
                        <span id="keepsake-photo-heading">
                          Add a photo to your Keepsake
                        </span>
                        <small>
                          Choose one favorite memory to tuck inside the card.
                        </small>
                      </div>

                      <span className="keepsakePhotoOptional">
                        Optional
                      </span>
                    </div>

                    {!keepsakePhotoUrl ? (
                      <label
                        className="keepsakePhotoAddButton"
                        htmlFor="keepsake-photo-input"
                      >
                        <ImagePlus size={18} />
                        Add a photo
                      </label>
                    ) : (
                      <div className="keepsakePhotoSelection">
                        <div className="keepsakePhotoThumbnail">
                          <img
                            src={keepsakePhotoUrl}
                            alt="Selected Keepsake preview"
                          />
                        </div>

                        <div className="keepsakePhotoSelectionCopy">
                          <strong>
                            Your photo is ready for the preview.
                          </strong>
                          <span>
                            {keepsakePhoto?.name || "Selected photo"}
                          </span>

                          <div className="keepsakePhotoActions">
                            <label
                              className="keepsakePhotoChangeButton"
                              htmlFor="keepsake-photo-input"
                            >
                              <ImagePlus size={15} />
                              Change photo
                            </label>

                            <button
                              type="button"
                              className="keepsakePhotoRemoveButton"
                              onClick={removeKeepsakePhoto}
                            >
                              <Trash2 size={15} />
                              Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    <input
                      id="keepsake-photo-input"
                      className="keepsakePhotoInput"
                      type="file"
                      accept="image/*"
                      onChange={handleKeepsakePhotoChange}
                    />

                    {keepsakePhotoMessage && (
                      <p
                        className="keepsakePhotoMessage"
                        role="status"
                      >
                        {keepsakePhotoMessage}
                      </p>
                    )}

                    <p className="keepsakePhotoTestNote">
                      Your photo stays on this device while you edit and is uploaded only when you create the Keepsake.
                    </p>
                  </section>
                )}

                <button
                  type="button"
                  className="premiumEnvelopeUnlockButton"
                  onClick={purchaseKeepsakeCollection}
                  disabled={
                    keepsakeUnlocked ||
                    keepsakePurchaseState === "loading" ||
                    keepsakePurchaseState === "purchasing" ||
                    keepsakePurchaseState === "restoring" ||
                    keepsakePurchaseState === "unavailable"
                  }
                  aria-label={
                    keepsakeUnlocked
                      ? "Keepsake Collection unlocked"
                      : "Unlock the Keepsake Collection"
                  }
                >
                  {keepsakeUnlocked ? (
                    <Check size={16} />
                  ) : (
                    <LockKeyhole size={16} />
                  )}

                  {keepsakeUnlocked
                    ? "Keepsake Collection Unlocked"
                    : keepsakePurchaseState === "purchasing"
                      ? "Opening Apple purchase..."
                      : keepsakePurchaseState === "loading"
                        ? "Loading Keepsake Collection..."
                        : `Unlock The Keepsake Collection${
                            keepsakeStorePrice
                              ? ` — ${keepsakeStorePrice}`
                              : ""
                          }`}
                </button>

                <div className="privatePreviewActionRow">
                  <button
                    type="button"
                    className="privateSecondaryButton"
                    onClick={restoreKeepsakePurchases}
                    disabled={
                      keepsakePurchaseState === "loading" ||
                      keepsakePurchaseState === "purchasing" ||
                      keepsakePurchaseState === "restoring"
                    }
                  >
                    {keepsakePurchaseState === "restoring"
                      ? "Restoring..."
                      : "Restore Purchases"}
                  </button>
                </div>

                {keepsakePurchaseMessage && (
                  <p
                    className="premiumEnvelopeComingSoon"
                    role="status"
                  >
                    {keepsakePurchaseMessage}
                  </p>
                )}

                <p className="premiumEnvelopeComingSoon">
                  <p className="premiumEnvelopeComingSoon">
  One-time purchase · All 6 designs · Add a photo · Yours to keep
</p>
                </p>
              </div>
            )}
          </div>

          <div className="privatePrivacyNotice">
            <LockKeyhole
              size={17}
            />

            <p>
              Private notes do not appear on
              the Community Wall. Anyone with
              the unique link can read the
              note, so only share the link
              with the person you intend.
            </p>
          </div>

          <div className="privateComposerActions">
            <button
              type="button"
              className="privateSecondaryButton"
              onClick={
                handlePreview
              }
              disabled={
                submitting ||
                !canCreateNote
              }
            >
              <Sparkles size={17} />

              Preview note
            </button>

            <button
              type={premiumThemeSelected ? "button" : "submit"}
              className="privatePrimaryButton"
              onClick={
                premiumThemeSelected
                  ? handlePreview
                  : undefined
              }
              disabled={
                submitting ||
                (!premiumThemeSelected && !canCreateNote)
              }
            >
              {premiumThemeSelected ? (
                <Sparkles size={17} />
              ) : (
                <Send size={17} />
              )}

              {premiumThemeSelected
                ? "Preview this Keepsake design"
                : submitting
                  ? "Creating your note..."
                  : "Create private love note"}
            </button>
          </div>

          {composerMessage && (
            <p
              className="privateStatus"
              role="status"
            >
              {composerMessage}
            </p>
          )}
        </form>

        <button
          type="button"
          className="privateBackLink privateBackLinkButton"
          onClick={
            returnToCommunityWall
          }
        >
          ← Back to the Community Wall
        </button>
      </section>
    </main>
  );
}

export default PrivateLoveNote;